import React from 'react';
import { Badge } from '../../../components/ui/badge';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow
} from '../../../components/ui/table';
import {
	REFERRAL_ATTRIBUTION_STATUS,
	REFERRAL_REWARD_STATUS,
	ReferralAttributionI,
	ReferralAttributionStatus,
	ReferralRewardI,
	RewardSnapshotI
} from '../../../api/referrals/interfaces/Referrals';

interface AttributionsTableProps {
	attributions: ReferralAttributionI[];
	rewards: ReferralRewardI[];
	rewardSnapshot: RewardSnapshotI;
}

// Badge per attribution status. When a reward is already applied, REWARD_APPLIED_BADGE takes precedence over this map.
// cancelled is never reached here — filtered before rendering (backend should also exclude it from the response).
const ATTRIBUTION_BADGE: Record<ReferralAttributionStatus, { className: string; label: string }> = {
	registered: {
		className: 'bg-yellow-100 text-yellow-700 hover:bg-yellow-100',
		label: 'Proceso iniciado'
	},
	converted: {
		className: 'bg-primary/20 text-primary hover:bg-primary/20',
		label: 'Plan traspasado'
	},
	reward_eligible: {
		className: 'bg-blue-100 text-blue-700 hover:bg-blue-100',
		label: 'Premio listo'
	},
	converted_no_reward: {
		className: 'bg-secondary/20 text-secondary hover:bg-secondary/20',
		label: 'Plan abierto'
	},
	cancelled: {
		className: 'bg-destructive/20 text-destructive hover:bg-destructive/20',
		label: 'Cancelado'
	}
};

const REWARD_APPLIED_BADGE = {
	className: 'bg-primary/20 text-primary hover:bg-primary/20',
	label: 'Premio redimido'
};

const formatDate = (isoDate: string | null): string => {
	if (!isoDate) return '—';
	return new Date(isoDate).toLocaleDateString('es-ES', {
		day: '2-digit',
		month: '2-digit',
		year: '2-digit'
	});
};

// Etiqueta de la columna "Premio redimido". Función pura para testeo exhaustivo
// y para evitar magic strings dispersas. Orden de prioridad:
//   1. Plan nuevo (sin premio) → mensaje fijo.
//   2. Premio aplicado → fecha de aplicación (o '—' si la fecha faltase por inconsistencia).
//   3. Esperando conversión → "Pendiente".
//   4. Cualquier otro estado (e.g. converted sin reward generado aún) → '—'.
interface RenderRedeemedCellInput {
	isConvertedNoReward: boolean;
	isRewardApplied: boolean;
	isAwaitingConversion: boolean;
	rewardAppliedAt: string | null;
}
export const renderRedeemedCell = ({
	isConvertedNoReward,
	isRewardApplied,
	isAwaitingConversion,
	rewardAppliedAt
}: RenderRedeemedCellInput): string => {
	if (isConvertedNoReward) return 'Premio solo para traspasos';
	if (isRewardApplied) return formatDate(rewardAppliedAt);
	if (isAwaitingConversion) return 'Pendiente';
	return '—';
};

const AttributionsTable: React.FC<AttributionsTableProps> = ({
	attributions,
	rewards,
	rewardSnapshot
}) => {
	// Index O(1) por attribution_id para no hacer find() dentro del map (evita O(n·m)).
	const rewardByAttributionId = new Map(rewards.map((r) => [r.attribution_id, r]));

	return (
		<div className="overflow-x-auto">
			<Table className="data-table">
				<TableHeader>
					<TableRow>
						<TableHead>Invitado</TableHead>
						<TableHead>Activado</TableHead>
						<TableHead>Estado</TableHead>
						<TableHead>Premio</TableHead>
						<TableHead>Premio redimido</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{attributions
						.filter((attr) => attr.status !== REFERRAL_ATTRIBUTION_STATUS.Cancelled)
						.map((attr) => {
							const reward = rewardByAttributionId.get(attr.id);
							const isConvertedNoReward =
								attr.status === REFERRAL_ATTRIBUTION_STATUS.ConvertedNoReward;
							// Guard: si la atribución no genera reward (plan nuevo), aunque por
							// inconsistencia llegara un reward 'applied' NO debemos mostrar el badge
							// "Premio redimido" — sería incongruente con la columna Premio "No aplica".
							const isRewardApplied =
								reward?.status === REFERRAL_REWARD_STATUS.Applied && !isConvertedNoReward;
							const badgeConfig = isRewardApplied
								? REWARD_APPLIED_BADGE
								: ATTRIBUTION_BADGE[attr.status];
							const premioLabel = isConvertedNoReward ? 'No aplica' : rewardSnapshot.display_name;
							const isAwaitingConversion =
								attr.status === REFERRAL_ATTRIBUTION_STATUS.RewardEligible ||
								attr.status === REFERRAL_ATTRIBUTION_STATUS.Registered;

							return (
								<TableRow key={attr.id}>
									<TableCell className="font-medium">
										{attr.referred_first_name
											? `${attr.referred_first_name} ${attr.referred_initial_surname ?? ''}`.trim()
											: 'Invitado'}
									</TableCell>
									<TableCell className="text-sm text-muted-foreground">
										{formatDate(
											attr.status === REFERRAL_ATTRIBUTION_STATUS.Registered
												? attr.registered_at
												: attr.converted_at
										)}
									</TableCell>
									<TableCell>
										<Badge className={badgeConfig.className}>{badgeConfig.label}</Badge>
									</TableCell>
									<TableCell className="text-sm font-medium">{premioLabel}</TableCell>
									<TableCell className="text-sm text-muted-foreground">
										{renderRedeemedCell({
											isConvertedNoReward,
											isRewardApplied,
											isAwaitingConversion,
											rewardAppliedAt: reward?.applied_at ?? null
										})}
									</TableCell>
								</TableRow>
							);
						})}
				</TableBody>
			</Table>
		</div>
	);
};

export default AttributionsTable;
